import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Share2, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import ImageCarousel from '@/components/ImageCarousel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogDetail = () => {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArticle();
  }, [id]);

  const fetchArticle = async () => {
    try {
      const response = await axios.get(`${API}/blogs/${id}`);
      setArticle(response.data);
    } catch (error) {
      console.error('Error fetching article:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: article.title,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  // Get all images for carousel (combine images array with legacy image_url)
  const getCarouselImages = () => {
    const images = [];
    if (article.images && article.images.length > 0) {
      images.push(...article.images);
    } else if (article.image_url) {
      images.push(article.image_url);
    }
    return images;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <div className="text-xl text-gray-600 mb-6">Article not found</div>
        <Link to="/blogs">
          <Button>Back to Blogs</Button>
        </Link>
      </div>
    );
  }

  const carouselImages = getCarouselImages();

  return (
    <div className="blog-detail-page">
      {/* Back Button */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <Link to="/blogs">
            <Button variant="ghost" className="hover:bg-gray-100">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Blogs
            </Button>
          </Link>
        </div>
      </div>

      {/* Article Content */}
      <article className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          {/* Header */}
          <div className="mb-10">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4 text-gray-500">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>{article.date}</span>
                </div>
                {article.author && (
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    <span>{article.author}</span>
                  </div>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleShare}
                className="rounded-full"
              >
                <Share2 className="h-4 w-4 mr-2" />
                Share
              </Button>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold mb-6 text-black">{article.title}</h1>
            
            {/* Tags */}
            {article.tags && article.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {article.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="bg-[#20B2AA]/10 text-[#20B2AA] px-3 py-1 rounded-full text-sm font-medium"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Image Carousel */}
          {carouselImages.length > 0 && (
            <div className="mb-10">
              <ImageCarousel images={carouselImages} />
            </div>
          )}

          {/* Content - Render HTML from rich text editor */}
          <div
            className="rich-content rich-content-lg"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Related Articles CTA */}
          <div className="mt-16 pt-10 border-t border-gray-200">
            <Link to="/blogs">
              <Button
                data-testid="more-blogs-btn"
                variant="outline"
                className="rounded-full hover:bg-gray-100"
              >
                View More Blogs
              </Button>
            </Link>
          </div>
        </div>
      </article>
    </div>
  );
};

export default BlogDetail;
